import { useEffect, useState } from 'react';
import { v4 as uuid } from 'uuid';
import FieldElemContainer from './field-element-container/FieldElementContainer';
import './AvroSchemaViewer.scss';
import { Field, TypeNode, Utils } from './Utils';
import { EventSchemaDetail } from '../../store/_api/TypeDefs';

  interface AvroSchemaViewerProps {
    eventDetails: EventSchemaDetail,
}

const AvroSchemaViewer = ({ eventDetails }: AvroSchemaViewerProps) => {
  const [fields, setFields] = useState<Field[]>([
    {
      id: uuid(),
      namespace: '',
      name: '',
      type: 'record',
    }
  ]);

  /**
   * @method updateFields
   * @description Updates the fields and sets the Avro schema.
   * @param fields - The array of fields to update.
   */
  const updateFields = (fields: Field[]) => {
    setFields(fields);
  };

  /**
   * @method recordTypeCheck
   * @description Checks if the field type is 'record' and if it has child fields.
   * Renders the child fields if they exist.
   * @param field - The field to check and render child fields for.
   * @returns The rendered child fields if they exist, otherwise null.
   */
  const recordTypeCheck = (field: Field) => (field.type === 'record' && field.fields && field.fields.length > 0
    && <div className="child-field">{field.fields?.map((property) => { property.id = property.id ?? uuid(); return renderFields(property, field); })}</div>
  );

  /**
   * @method arrayOrRecordTypeCheck
   * @description Checks if the field is an array or record type and renders the corresponding fields.
   * @param field The field to check.
   * @returns The rendered fields if the field is an array or record type, otherwise an empty string.
   */
  const arrayOrRecordTypeCheck = (field: Field) => (field.type as Array<TypeNode>).map((nodeType) => {
    if (typeof nodeType === 'object') {
      if (nodeType?.type && nodeType.type === 'record' && nodeType.fields) {
        return (<div key={`${field.id}-record`} className="child-field">{nodeType.fields?.map((property) => { property.id = property.id ?? uuid(); return renderFields(property, field); })}</div>);
      }
    }
    return '';
  });

  /**
   * @method arrayTypeCheck
   * @description Checks if the field is of array type and renders the array field.
   * @param field - The field to check.
   * @returns The rendered array field or an empty string.
   */
  const arrayTypeCheck = (field: Field) => (field.type as Array<TypeNode>).map((nodeType) => {
    if (nodeType && nodeType.type === 'array' && nodeType.items) {
      return (renderArrayField(nodeType as Field, field));
    }
    return '';
  });

  /**
   * @method arrayTypeCheck
   * @description Returns the content to render for a given field.
   * @param field - The field object.
   * @returns The rendered content for the field.
   */
  const getRenderFieldContent = (field: Field) => {
    if (field.type === 'record') {
      return recordTypeCheck(field);
    }

    if (field.type === 'array') {
      return (renderArrayField(field, field));
    }

    if (Array.isArray(field.type) && Utils.checkTypes(field.type, 'record')) {
      return arrayOrRecordTypeCheck(field);
    }

    if (Array.isArray(field.type) && Utils.checkTypes(field.type, 'array')) {
      return arrayTypeCheck(field);
    }

    if (Utils.checkTypes(field.type, 'record')) {
      const nodeType = field as TypeNode;
      if (nodeType?.type && typeof nodeType.type === 'object' && !Array.isArray(nodeType.type) && nodeType.type.type === 'record' && nodeType.type.fields) {
        return (<div key={`${field.id}-record`} className="child-field">{nodeType.type.fields?.map((property) => { property.id = property.id ?? uuid(); return renderFields(property, field); })}</div>);
      }
    }

    if (Utils.checkTypes(field.type, 'array')) {
      const nodeType = field as TypeNode;
      if (nodeType && (nodeType.type as TypeNode).type === 'array' && (nodeType.type as TypeNode).items) {
        return (renderArrayField(nodeType.type as Field, field));
      }
    }
    return '';
  };

  /**
   * @method renderArrayField
   * @description Renders an array field.
   * @param nodeItems - The array of items to render.
   * @param field - The parent field.
   * @returns The rendered array field.
   */
  const renderArrayField = (nodeItems: Field, field: Field) => {
    if (Array.isArray(nodeItems.items)) {
      return nodeItems.items.map((item) => {
        if (typeof item === 'object') {
          item.id = item.id ?? uuid();
          return <div key={`${field.id}-array`} className="child-field">{renderFields(item as Field, field)}</div>;
        }
        return '';
      });
    }
    return '';
  };

  /**
   * @method renderFields
   * @description Renders the fields for a given field and its parent field.
   * @param field - The field to render.
   * @param parentField - The parent field of the current field.
   * @returns The rendered fields.
   */
  const renderFields = (field: Field, parentField: Field | null) => (
    <div
      key={field.id}
      className="parent-field"
      id={`field-${field.id}`}
    >
      <FieldElemContainer
        field={field}
        parentField={parentField}
        fields={fields}
        sensitiveFields={eventDetails.sensitiveFields || []}
      />
      {
        getRenderFieldContent(field)
      }

    </div>
  );

  // This useEffect triggers when we have avroSchema and we click on build avro schema again
  useEffect(() => {
    if (eventDetails.schema) {
      updateFields([JSON.parse(eventDetails.schema)]);
    }
  }, [eventDetails.schema]);

  return (
    <div className="container-fluid avro-schema-builder">
      <div className="row">
        <div className="main-root">
          {fields.map((field) => renderFields(field, null))}
        </div>
      </div>
    </div>
  );
};

export default AvroSchemaViewer;
